import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { Mail, ArrowLeft, Loader2, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email', 'sent'
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Countdown timer for resend cooldown
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre adresse e-mail');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      toast.success('Si cette adresse existe, vous recevrez un lien de réinitialisation.');
      setStep('sent');
      setResendCooldown(60);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la demande'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendLink = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-reset-link`, { email });
      toast.success('Un nouveau lien a été envoyé');
      setResendCooldown(60);
    } catch (error) {
      toast.error('Erreur lors du renvoi du lien');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E4A3D] to-[#1a2e25] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/login" className="inline-flex items-center text-sm text-stone-500 hover:text-[#2E4A3D] mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
          <CardTitle className="text-2xl font-serif text-[#2E4A3D]">
            {step === 'sent' ? 'Email envoyé !' : 'Mot de passe oublié'}
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          {/* Step 1: Email input */}
          {step === 'email' && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <p className="text-stone-600 text-sm text-center mb-6">
                Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="email">Adresse e-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="pl-10"
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  'Envoyer le lien'
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Link sent confirmation */}
          {step === 'sent' && (
            <div className="space-y-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <div className="text-center">
                <p className="text-stone-600 mb-2">
                  Si un compte existe avec cette adresse, vous recevrez un email à :
                </p>
                <p className="font-medium text-[#2E4A3D] mb-4">{email}</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-medium">Prochaines étapes</p>
                    <ol className="text-blue-700 text-sm mt-1 space-y-1 list-decimal list-inside">
                      <li>Consultez votre boîte de réception</li>
                      <li>Cliquez sur le lien dans l'email</li>
                      <li>Choisissez votre nouveau mot de passe</li>
                    </ol>
                    <p className="text-blue-600 text-xs mt-2">
                      Le lien est valable <strong>72 heures</strong>.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 text-sm">
                  💡 Pensez à vérifier votre dossier <strong>spam/indésirables</strong>.
                </p>
              </div>
              
              <div className="text-center text-stone-600">
                <p className="mb-2">Vous n'avez pas reçu l'email ?</p>
                <button
                  onClick={handleResendLink}
                  disabled={resendCooldown > 0 || loading}
                  className={`inline-flex items-center gap-2 font-medium ${
                    resendCooldown > 0 
                      ? 'text-stone-400 cursor-not-allowed' 
                      : 'text-[#2E4A3D] hover:underline'
                  }`}
                  data-testid="resend-link"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {resendCooldown > 0 
                    ? `Renvoyer dans ${resendCooldown}s` 
                    : 'Renvoyer le lien'}
                </button>
              </div>
              
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
                data-testid="back-to-login"
              >
                Retour à la connexion
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
